import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {
    Box,
    Typography,
    Alert,
    Autocomplete,
    TextField,
    CircularProgress,
    Checkbox,
    Stack
} from '@mui/material';
import PropTypes from 'prop-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import {useGetFormAccessQuery, useManageFormAccessMutation} from '../../../api/services/formsService';
import {useGetActiveUsersQuery} from '../../../api/services/accountService';
import {ROLES, ROLES_LABELS} from '../../../constants/roles';
import useNotification from '../../../utility/hooks/useNotification';
import SettingsSectionBase from './PrivateFormSettings/SettingsSection';

const FormAccessSettings = React.forwardRef(({
                                                 formId,
                                                 formName,
                                                 isFormCreation = false,
                                                 wizardMode = false,
                                                 embedded = false
                                             }, ref) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRoleNames, setSelectedRoleNames] = useState([]);
    const usersPrefilledRef = useRef(false);
    const rolesPrefilledRef = useRef(false);
    const [manageFormAccess, {isLoading: isManaging}] = useManageFormAccessMutation();
    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();

    const {
        data: availableUsers = [],
        isLoading: isLoadingUsers
    } = useGetActiveUsersQuery({pageSize: 0}, {
        skip: !formId
    });

    const filteredRoleNames = [
        ROLES.SCHOOL_ADMIN,
        ROLES.MEMBER_OF_STAFF,
        ROLES.SENIOR_MEMBER_OF_STAFF
    ];

    const {
        data: formAccess = {},
        isLoading: isLoadingFormAccess
    } = useGetFormAccessQuery(formId, {
        skip: !formId
    });

    const accessEntry = Array.isArray(formAccess)
        ? (formAccess[0] || {})
        : (formAccess || {});
    const serverUserIds = Array.isArray(accessEntry.userIds)
        ? accessEntry.userIds
        : (Array.isArray(accessEntry.users) ? accessEntry.users : []);

    const serverRoleNames = Array.isArray(accessEntry.roles)
        ? accessEntry.roles
        : [];

    useEffect(() => {
        if (formId) {
            setSelectedUsers([]);
            setSelectedRoleNames([]);
            usersPrefilledRef.current = false;
            rolesPrefilledRef.current = false;
        }
    }, [formId]);

    useEffect(() => {
        if (
            formId &&
            !usersPrefilledRef.current &&
            selectedUsers.length === 0 &&
            availableUsers.length > 0 &&
            serverUserIds.length > 0
        ) {
            const currentUsers = serverUserIds
                .map(userId => availableUsers.find(user => user.id === userId))
                .filter(Boolean);
            setSelectedUsers(currentUsers);
            usersPrefilledRef.current = true;
        }
    }, [formId, availableUsers, serverUserIds, selectedUsers.length]);

    useEffect(() => {
        if (
            formId &&
            !rolesPrefilledRef.current &&
            selectedRoleNames.length === 0 &&
            filteredRoleNames.length > 0 &&
            serverRoleNames.length > 0
        ) {
            const allowedLower = filteredRoleNames.map(n => String(n).toLowerCase());
            const currentRoleNames = serverRoleNames
                .filter(roleName => allowedLower.includes(String(roleName).toLowerCase()));
            setSelectedRoleNames(currentRoleNames);
            rolesPrefilledRef.current = true;
        }
    }, [formId, filteredRoleNames, serverRoleNames, selectedRoleNames.length]);

    const handleSave = async ({suppressSuccessNotification = false} = {}) => {
        if (!formId) {
            return;
        }

        if (selectedUsers.length === 0 && selectedRoleNames.length === 0) {
            showErrorNotification('At least one user or role must be selected');
            throw new Error('At least one user or role must be selected');
        }

        try {
            const userIds = selectedUsers.map(user => user.id);

            await manageFormAccess({
                formId,
                userIds,
                roles: selectedRoleNames
            }).unwrap();
            if (!wizardMode && !suppressSuccessNotification) {
                showSuccessNotification('Form access updated successfully');
            }
        } catch (error) {
            showErrorNotification('Failed to update form access');
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        saveAccessSettings: handleSave
    }));

    const content = (
        <Stack spacing={3}>
            {!wizardMode && !embedded && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Form Access Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage which users and roles have access to this form. This controls who can view the form,
                        access form responses, and manage the form settings.
                    </Typography>
                </Box>
            )}

            {!formId && embedded && (
                <Alert severity="info">
                    Save the form first to manage which users and roles can access it.
                </Alert>
            )}

            <Alert severity="info">
                <Typography variant="body2">
                    <strong>Note:</strong> When you share this form with other users, they will be able to see the names
                    of all groups assigned to this form in the form settings, even if they don&apos;t have access to
                    those groups. The groups themselves will not be available until they are explicitly shared with
                    these users.
                </Typography>
            </Alert>

            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 'bold'
                }}>
                    Users
                </Typography>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    options={availableUsers}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedUsers}
                    onChange={(event, newValue) => {
                        if (newValue.length === 0 && selectedUsers.length > 0 && selectedRoleNames.length === 0) {
                            showErrorNotification('At least one user or role must remain in the form');
                            return;
                        }
                        setSelectedUsers(newValue);
                    }}
                    loading={isLoadingUsers || isLoadingFormAccess}
                    disabled={isManaging || !formId}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Users"
                            placeholder="Start typing a name..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(isLoadingUsers || isLoadingFormAccess) ? <CircularProgress size={20}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option, {selected}) => (
                        <li {...props} key={option.id}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                                checkedIcon={<CheckBoxIcon fontSize="small"/>}
                                checked={selected}
                            />
                            {option.name}
                        </li>
                    )}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 'bold'
                }}>
                    Roles
                </Typography>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    options={filteredRoleNames}
                    getOptionLabel={(name) => ROLES_LABELS[name] || name || ''}
                    isOptionEqualToValue={(option, value) => String(option || '').toLowerCase() === String(value || '').toLowerCase()}
                    value={selectedRoleNames}
                    onChange={(event, newValue) => {
                        if (newValue.length === 0 && selectedRoleNames.length > 0 && selectedUsers.length === 0) {
                            showErrorNotification('At least one user or role must remain in the form');
                            return;
                        }
                        setSelectedRoleNames(newValue);
                    }}
                    loading={isLoadingFormAccess}
                    disabled={isManaging || !formId}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Roles"
                            placeholder="Select roles..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoadingFormAccess ? <CircularProgress size={20}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, name, {selected}) => (
                        <li {...props} key={name}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                                checkedIcon={<CheckBoxIcon fontSize="small"/>}
                                checked={selected}
                            />
                            {ROLES_LABELS[name] || name}
                        </li>
                    )}
                />
            </Box>
        </Stack>
    );

    if (embedded) {
        return (
            <SettingsSectionBase
                header="Form Access"
                headerIcon={<AdminPanelSettingsOutlinedIcon fontSize="small"/>}
                subHeader="Manage which users and roles can view responses and manage this form."
            >
                {content}
            </SettingsSectionBase>
        );
    }

    return (
        <Box sx={{p: 3}}>
            {content}
        </Box>
    );
});

FormAccessSettings.displayName = 'FormAccessSettings';

FormAccessSettings.propTypes = {
    formId: PropTypes.number,
    formName: PropTypes.string,
    isFormCreation: PropTypes.bool,
    wizardMode: PropTypes.bool,
    embedded: PropTypes.bool
};

export default FormAccessSettings;
